<template>
  <div v-if="visible" class="modal" @click.self="$emit('close')">
    <div class="modal-content">
      <div class="modal-header">
        <h3>{{ detail.name }}</h3>
        <button type="button" class="modal-close" @click="$emit('close')" aria-label="关闭">&times;</button>
      </div>
      <div class="modal-body" v-if="detail.id">
        <!-- 头部：封面 + 标题 + 版面费(红) + 分类/刊期/卖点，右侧总署/收录按钮 -->
        <div class="detail-hero">
          <div class="detail-hero-main">
            <div class="detail-cover">
              <img v-if="detail.cover_image" :src="detail.cover_image" :alt="detail.name" />
              <div v-else class="cover-placeholder">封面</div>
            </div>
            <div class="detail-hero-right">
              <h2 class="detail-title">{{ detail.name }}</h2>
              <p class="detail-fee">版面费：<span class="fee-num">{{ feeText }}</span> <span v-if="detail.page_chars" class="detail-chars-inline">{{ detail.page_chars }}</span><span v-if="detail.extra_fee_desc" class="detail-extra-desc">, {{ detail.extra_fee_desc }}</span></p>
              <p class="detail-meta">{{ metaLine }}</p>
              <p v-if="detail.current_issue" class="detail-issue"><span class="label">当前刊期：</span>{{ detail.current_issue }}</p>
              <p v-if="detail.selling_points" class="detail-selling"><span class="label">卖点：</span>{{ detail.selling_points }}</p>
            </div>
            <div class="detail-hero-actions">
              <a v-if="detail.gapp_link" :href="detail.gapp_link" target="_blank" rel="noopener" class="btn-link">总署链接</a>
              <a v-if="detail.inclusion_url" :href="detail.inclusion_url" target="_blank" rel="noopener" class="btn-link">收录网址</a>
            </div>
          </div>
          <div class="detail-tags-row" v-if="tagsList.length">
            <span v-for="(t, idx) in tagsList" :key="(t || '') + '-' + idx" class="detail-tag">{{ t }}</span>
          </div>
        </div>

        <!-- 两栏网格：同行等高 -->
        <div class="detail-grid">
          <!-- 第1行：基本信息(两子列) | 期刊分析(两子列) -->
          <section class="info-card grid-cell">
            <h4>基本信息</h4>
            <div class="info-two-col">
              <div class="info-col">
                <div v-for="item in basicLeft" :key="item.label" class="info-row">
                  <span class="info-label">{{ item.label }}：</span>
                  <span class="info-value">{{ item.val || '无' }}</span>
                </div>
              </div>
              <div class="info-col">
                <div v-for="item in basicRight" :key="item.label" class="info-row">
                  <span class="info-label">{{ item.label }}：</span>
                  <span class="info-value">{{ item.val || '无' }}</span>
                </div>
              </div>
            </div>
          </section>
          <section class="info-card grid-cell">
            <h4>期刊分析</h4>
            <div class="info-two-col">
              <div class="info-col">
                <div v-for="item in analysisLeft" :key="item.label" class="info-row">
                  <span class="info-label">{{ item.label }}：</span>
                  <span class="info-value" v-if="item.label === '审稿难度'">
                    <span class="star-row"><span v-for="i in 5" :key="i" class="star" :class="{ on: i <= reviewStars }">★</span></span>
                    <span v-if="item.val">{{ item.val }}</span>
                  </span>
                  <span v-else class="info-value">{{ item.val || '无' }}</span>
                </div>
              </div>
              <div class="info-col">
                <div v-for="item in analysisRight" :key="item.label" class="info-row">
                  <span class="info-label">{{ item.label }}：</span>
                  <span class="info-value">{{ item.val || '无' }}</span>
                </div>
              </div>
            </div>
          </section>

          <!-- 第2行：独立信息行 左 | 右 -->
          <section class="info-card grid-cell info-standalone">
            <div v-for="item in standaloneLeft" :key="item.label" class="info-row">
              <span class="info-label">{{ item.label }}：</span>
              <span class="info-value">{{ item.val || '无' }}</span>
            </div>
          </section>
          <section class="info-card grid-cell info-standalone">
            <div v-for="item in standaloneRight" :key="item.label" class="info-row">
              <span class="info-label">{{ item.label }}：</span>
              <span class="info-value">{{ item.val || '无' }}</span>
            </div>
          </section>

          <!-- 第3行：特殊要求 | 过稿率主观评价 -->
          <section class="info-card grid-cell info-card-highlight highlight-orange" v-if="detail.special_requirements">
            <h4>特殊要求</h4>
            <p class="block-text">{{ detail.special_requirements }}</p>
          </section>
          <section v-else class="info-card grid-cell info-card-placeholder"></section>
          <section class="info-card grid-cell info-card-highlight highlight-yellow" v-if="detail.pass_rate_evaluation">
            <h4>过稿率主观评价</h4>
            <p class="block-text">{{ detail.pass_rate_evaluation }}</p>
          </section>
          <section v-else class="info-card grid-cell info-card-placeholder"></section>

          <!-- 第4行：收稿范围 | 期刊关键词/空 -->
          <section class="info-card grid-cell" v-if="detail.scope_of_submission">
            <h4>收稿范围</h4>
            <p class="block-text">{{ detail.scope_of_submission }}</p>
          </section>
          <section v-else class="info-card grid-cell info-card-placeholder"></section>
          <section class="info-card grid-cell" v-if="detail.journal_keywords">
            <h4>期刊关键词</h4>
            <p class="block-text keywords-text">{{ detail.journal_keywords }}</p>
          </section>
          <section v-else class="info-card grid-cell info-card-placeholder"></section>

          <!-- 第5行：栏目(左) | 占位(右)，仅在有栏目时渲染 -->
          <template v-if="detail.sections_text">
            <section class="info-card grid-cell">
              <h4>栏目</h4>
              <p class="block-text">{{ detail.sections_text }}</p>
            </section>
            <section class="info-card grid-cell info-card-placeholder"></section>
          </template>
        </div>
      </div>
      <div class="modal-body loading-state" v-else>
        <span class="loading-dot">加载中…</span>
      </div>
    </div>
  </div>
</template>

<script>
import { computed } from 'vue'
const BASIC_LEFT = [
  ['sponsor', '主办'],
  ['supervisor', '主管'],
  ['page_chars', '版面字符'],
  ['plagiarism_requirement', '查重要求'],
  ['impact_factor', '影响因子']
]
const BASIC_RIGHT = [
  ['cn_number', 'CN'],
  ['issn_number', 'ISSN'],
  ['pages', '页数'],
  ['articles_per_issue', '每期发文量'],
  ['postal_code', '邮发代号']
]
const STANDALONE_LEFT = [
  ['region_restriction', '地区限制'],
  ['unit_restriction', '单位限制'],
  ['title_education_restriction', '限制职称、学历'],
  ['author_profile', '作者简介']
]
const ANALYSIS_LEFT = [
  ['review_difficulty', '审稿难度'],
  ['expedited_info', '加急版面'],
  ['has_internal_invoice', '社内发票'],
  ['online_presence', '上网情况'],
  ['copyright_phone', '版权页电话']
]
const ANALYSIS_RIGHT = [
  ['review_duration', '审稿时长'],
  ['fund_topic_requirement', '基金/课题要求'],
  ['academic_catalog', '学术目录'],
  ['query_method', '查稿方式'],
  ['worry_free_service', '无忧服务']
]
const STANDALONE_RIGHT = [
  ['marketing_tags', '营销标签'],
  ['rejection_directions', '拒收方向'],
  ['communication_medium', '传播载体'],
  ['publication_format', '出版形式'],
  ['thesis_format', '论文编排格式']
]
export default {
  name: 'JournalDetail',
  props: {
    visible: Boolean,
    detail: { type: Object, default: () => ({}) }
  },
  emits: ['close'],
  setup(props) {
    const feeText = computed(() => {
      if (!props.detail.price_visible) return '登录后可见'
      const a = props.detail.price_min ?? props.detail.price
      const b = props.detail.price_max
      const label = props.detail.price_label
      let s = ''
      if (a != null && b != null && a !== b) s = `¥${a}-${b}元`
      else if (a != null) s = `¥${a}元`
      else s = '暂无'
      return label ? `${label} ${s}` : s
    })
    const metaLine = computed(() => {
      const d = props.detail
      const parts = [
        d.level,
        d.index_db,
        d.publish_cycle,
        (d.direction && d.direction.name) || ''
      ].filter(Boolean)
      return parts.join(' / ')
    })
    const reviewStars = computed(() => {
      const d = props.detail.review_difficulty
      if (!d) return 0
      const m = String(d).match(/(\d)/)
      return m ? Math.min(5, Math.max(0, parseInt(m[1], 10))) : 0
    })
    function formatVal(key, v) {
      if (v === 0 || v === 1) return v ? '是' : '否'
      return v
    }
    const basicLeft = computed(() =>
      BASIC_LEFT.map(([key, label]) => ({ label, val: props.detail[key] }))
    )
    const basicRight = computed(() =>
      BASIC_RIGHT.map(([key, label]) => ({ label, val: props.detail[key] }))
    )
    const analysisLeft = computed(() =>
      ANALYSIS_LEFT.map(([key, label]) => ({ label, val: formatVal(key, props.detail[key]) }))
    )
    const analysisRight = computed(() =>
      ANALYSIS_RIGHT.map(([key, label]) => ({ label, val: formatVal(key, props.detail[key]) }))
    )
    const standaloneLeft = computed(() =>
      STANDALONE_LEFT.map(([key, label]) => ({ label, val: props.detail[key] }))
    )
    const standaloneRight = computed(() =>
      STANDALONE_RIGHT.map(([key, label]) => ({ label, val: formatVal(key, props.detail[key]) }))
    )
    const tagsList = computed(() => {
      const d = props.detail
      if (d.tags_arr && d.tags_arr.length) return d.tags_arr
      if (d.tags) return String(d.tags).split(',').map(s => s.trim()).filter(Boolean)
      return []
    })
    return {
      feeText,
      metaLine,
      basicLeft,
      basicRight,
      analysisLeft,
      analysisRight,
      standaloneLeft,
      standaloneRight,
      tagsList,
      reviewStars
    }
  }
}
</script>

<style scoped>
.modal {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(0,0,0,.5);
}
.modal-content {
  background: #fff;
  border-radius: var(--radius-lg);
  max-width: 1220px;
  width: 100%;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: var(--shadow-lg);
}
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 24px;
  border-bottom: 1px solid var(--color-border);
  background: var(--color-bg);
}
.modal-header h3 { margin: 0; font-size: 18px; font-weight: 600; }
.modal-close {
  border: none;
  background: none;
  font-size: 24px;
  cursor: pointer;
  color: var(--color-text-muted);
  line-height: 1;
  min-width: var(--touch-min);
  min-height: var(--touch-min);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--radius);
  transition: color 0.2s, background 0.2s;
}
.modal-close:hover { color: var(--color-text); background: rgba(0,0,0,.05); }
.modal-body {
  padding: 24px;
  overflow-y: auto;
  font-size: 13px;
  color: #666;
  background: #f0f2f5;
  -webkit-overflow-scrolling: touch;
}
@media (max-width: 768px) {
  .modal { padding: 0; align-items: stretch; }
  .modal-content {
    max-width: none;
    max-height: none;
    height: 100vh;
    height: 100dvh;
    border-radius: 0;
  }
  .modal-header { padding: 12px 16px; padding-top: calc(12px + var(--safe-top)); }
  .modal-header h3 { font-size: 16px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; margin-right: 8px; }
  .modal-body { padding: 16px; padding-bottom: calc(16px + var(--safe-bottom)); }
  .detail-hero { padding: 16px; }
  .detail-hero-main { flex-direction: column; margin-bottom: 12px; }
  .detail-cover { width: 80px; min-width: 80px; height: 106px; margin: 0 auto; }
  .detail-title { font-size: 16px; text-align: center; }
  .detail-hero-right { text-align: center; }
  .detail-actions { justify-content: center; }
  .detail-two-col { grid-template-columns: 1fr; gap: 16px; }
  .modal-body { background: #fff; }
}
/* 头部卡片：白底 + 阴影，参考图主内容区 */
.detail-hero {
  margin-bottom: 24px;
  padding: 22px;
  background: #fff;
  border-radius: var(--radius-lg);
  box-shadow: 0 2px 12px rgba(0,0,0,.08);
  border: 1px solid rgba(0,0,0,.06);
}
.detail-hero-main { display: flex; gap: 22px; margin-bottom: 18px; }
.detail-cover {
  width: 100px;
  min-width: 100px;
  height: 132px;
  border-radius: var(--radius);
  overflow: hidden;
  background: #f0f0f0;
  flex-shrink: 0;
}
.detail-cover img { width: 100%; height: 100%; object-fit: cover; }
.detail-cover .cover-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  color: var(--color-text-muted);
}
.detail-hero-right { flex: 1; min-width: 0; }
.detail-title {
  margin: 0 0 12px;
  font-size: 19px;
  font-weight: 700;
  color: #1a1a1a;
  line-height: 1.35;
}
.detail-fee { margin: 0 0 8px; font-size: 14px; color: #333; }
.detail-fee .fee-num { color: #c00; font-weight: 700; font-size: 16px; }
.detail-meta { margin: 0 0 6px; font-size: 13px; color: var(--color-text-secondary); }
.detail-chars { margin: 0 0 4px; font-size: 13px; color: #555; }
.detail-issue, .detail-selling { margin: 0 0 4px; font-size: 13px; color: #555; }
.detail-hero .label { color: #333; font-weight: 500; }
/* 操作标签：浅蓝/青绿圆角按钮，参考图 */
.detail-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  padding-top: 16px;
  border-top: 1px solid #eee;
}
.detail-action {
  padding: 8px 16px;
  font-size: 12px;
  color: #fff;
  background: #5cadff;
  border: none;
  border-radius: 20px;
  cursor: default;
  font-weight: 500;
}
[data-theme="warm"] .detail-action { background: #e6a23c; }
[data-theme="fresh"] .detail-action { background: #409eff; }
/* 头部右侧按钮：总署链接、收录网址 */
.detail-hero-actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex-shrink: 0;
}
.btn-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 18px;
  font-size: 13px;
  color: #fff;
  background: #5cadff;
  border-radius: 8px;
  text-decoration: none;
  white-space: nowrap;
  min-height: var(--touch-min);
}
[data-theme="warm"] .btn-link { background: #e6a23c; }
[data-theme="fresh"] .btn-link { background: #409eff; }
.btn-link:hover { opacity: 0.9; }
.detail-chars-inline { color: #555; font-weight: normal; }
.detail-extra-desc { color: #666; font-size: 13px; }
/* 标签行 */
.detail-tags-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding-top: 14px;
  border-top: 1px solid #eee;
  margin-top: 14px;
}
.detail-tag {
  padding: 4px 10px;
  font-size: 12px;
  color: #666;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 4px;
}
/* 两栏网格：同行等高 */
.detail-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  align-items: stretch;
}
.detail-grid .grid-cell {
  min-height: 0;
}
.info-card {
  padding: 20px;
  background: #fff;
  border-radius: var(--radius-lg);
  box-shadow: 0 2px 12px rgba(0,0,0,.08);
  border: 1px solid rgba(0,0,0,.06);
  display: flex;
  flex-direction: column;
}
.info-card h4 {
  margin: 0 0 16px;
  font-size: 15px;
  font-weight: 600;
  color: #1a1a1a;
  padding-bottom: 10px;
  border-bottom: 1px solid #eee;
  flex-shrink: 0;
}
.info-card-placeholder {
  min-height: 80px;
  opacity: 0.6;
}
.info-two-col {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px 20px;
  flex: 1;
  min-height: 0;
}
.info-col { display: flex; flex-direction: column; gap: 12px; min-width: 0; }
.info-standalone { gap: 12px; }
.info-standalone .info-row { margin: 0; }
.info-card-highlight {
  border-left: 4px solid #e6a23c;
}
.highlight-orange {
  background: linear-gradient(135deg, #ff9a56 0%, #ff7e3d 100%);
  color: #fff;
  border-left-color: #e85a00;
}
.highlight-orange h4 { color: #fff; border-bottom-color: rgba(255,255,255,.35); }
.highlight-orange .block-text { color: rgba(255,255,255,.95); }
.highlight-yellow {
  background: #fffbe6;
  border-left-color: #e6a23c;
}
[data-theme="fresh"] .highlight-yellow { background: #ecf5ff; border-left-color: #409eff; }
.info-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 13px;
  line-height: 1.55;
}
.info-label { flex-shrink: 0; color: #666; min-width: 90px; }
.info-value { color: #333; word-break: break-all; }
.star-row { display: inline-flex; letter-spacing: 2px; margin-right: 6px; }
.star { color: #e0e0e0; }
.star.on { color: #f7ba2a; }
.block-text { margin: 0; line-height: 1.75; color: #555; flex: 1; }
.keywords-text { word-break: break-all; }
.loading-state { display: flex; align-items: center; justify-content: center; min-height: 120px; }
.loading-dot { color: var(--color-text-muted); }
@media (max-width: 768px) {
  .detail-grid { grid-template-columns: 1fr; }
  .detail-hero-main { flex-wrap: wrap; }
  .detail-hero-actions { flex-direction: row; }
}
</style>
